import { formatDateToISOString } from '../../shared/helpers/date.func';
import {
	ApiCategoryAssitanceResponses,
	ApiCreateLek,
	ApiGetLekCategories,
	ApiLek,
	ApiLekDateRangePossibilities,
	ApiLekSponsor,
	ApiUpdateLek,
} from '../api-types/lek.api';
import {
	AppCreateLek,
	AppUpdateLek,
	CategoryAssistanceResponse,
	Lek,
	LekCategories,
	LekDateRangePossibilities,
	LekSponsor,
} from '../app-types/lek.app';
import { transformApiInstitutionBranchToAppInstitutionBranch } from './instition.tranform';
import { transformApiWorkplaceToAppWorkplace } from './workplace-transform';

export function transformApiGetLekCategories(input: ApiGetLekCategories): LekCategories {
	const output: LekCategories = {
		institutionBranch: input.institutionBranch
			? transformApiInstitutionBranchToAppInstitutionBranch(input.institutionBranch)
			: undefined,
		categoryAssistanceResponses: input.categoryAssistanceResponses.entities.map((response) =>
			transformApiCategoryAssistanceResponse(response),
		),
	};
	return output;
}

export function transformApiCategoryAssistanceResponse(
	input: ApiCategoryAssitanceResponses,
): CategoryAssistanceResponse {
	const output: CategoryAssistanceResponse = {
		id: input.id,
		requiredEmploymentType: input.requiredEmploymentType,
		requiredSponsor: input.requiredSponsor,
		description: input.description,
		canEnterDuration: input.canEnterDuration ?? false,
	};
	return output;
}

export function transformAppCreateLekToApiCreateLek(input: AppCreateLek) {
	const output: ApiCreateLek = {
		startDate: formatDateToISOString(input.startDate)!,
		endDate: formatDateToISOString(input?.endDate),
		result: input.result,
		categoryAssistanceId: input.categoryAssistanceId,
		assignmentId: input.assignmentId,
		categorySponsorId: input.appCreateLekDynamicFields?.sponsorId,
		workplaceId: input.appCreateLekDynamicFields?.workplaceId,
		isHandledByExternalSponsor: input.appCreateLekDynamicFields?.isHandledByExternalSponsor,
		duration: input.duration,
	};
	return output;
}

export function transformAppUpdateLekToApiUpdateLek(input: AppUpdateLek) {
	const output: ApiUpdateLek = {
		startDate: formatDateToISOString(input.startDate)!,
		endDate: formatDateToISOString(input?.endDate),
		result: input.result,
		categoryAssistanceId: input.categoryAssistanceId,
		assignmentId: input.assignmentId,
		categorySponsorId: input.appUpdateLekDynamicFields?.sponsorId,
		workplaceId: input.appUpdateLekDynamicFields?.workplaceId,
		isHandledByExternalSponsor: input.appUpdateLekDynamicFields?.isHandledByExternalSponsor,
		duration: input.duration,
	};
	return output;
}

export function transformApiLekDatePossibilitiesToAppLekDateRangePossibilities(
	input: ApiLekDateRangePossibilities,
): LekDateRangePossibilities {
	const output: LekDateRangePossibilities = {
		validStartDateRange: {
			min: input.validStartDateRange.min ? new Date(input.validStartDateRange.min + 'T00:00') : undefined,
			max: input.validStartDateRange.max ? new Date(input.validStartDateRange.max + 'T00:00') : undefined,
		},
		validEndDateRange: {
			min: input.validEndDateRange.min ? new Date(input.validEndDateRange.min + 'T00:00') : undefined,
			max: input.validEndDateRange.max ? new Date(input.validEndDateRange.max + 'T00:00') : undefined,
		},
	};
	return output;
}

export function transformApiLekToAppLek(input: ApiLek): Lek {
	const output: Lek = {
		id: input.id,
		startDate: input.startDate ? new Date(input.startDate) : undefined,
		endDate: input.endDate ? new Date(input.endDate) : undefined,
		result: input.result ?? '',
		workplace: input.workplace ? transformApiWorkplaceToAppWorkplace(input.workplace) : undefined,
		sponsor: input.categorySponsor
			? {
					id: input.categorySponsor.id,
					name: input.categorySponsor.description,
					integrationOfficeId: -1,
				}
			: undefined,
		categoryAssistance: transformApiCategoryAssistanceResponse(input.categoryAssistance),
		isHandledByExternalSponsor: input.isHandledByExternalSponsor,
		duration: input.duration,
	};
	return output;
}

export function transformApiLeksToAppLeks(input: ApiLek[]): Lek[] {
	return input.map((lek) => transformApiLekToAppLek(lek));
}

export function transformApiLekSponsorsToAppLekSponsors(input: ApiLekSponsor[]): LekSponsor[] {
	return (input ?? []).map((sponsor) => ({
		id: sponsor.id,
		description: sponsor.description,
	}));
}

/**
 * Maps the name of a control in our form to the corresponding field name expected by the API for LEKs.
 * @param fieldName Name of the field inside our forms
 * @returns name of the field coming from the API
 */
export const mapEditFormFieldNameToEditableLekMetadataField = (fieldName: string): string => {
	const fieldMapping: Record<string, string> = {
		startDate: 'startDate',
		endDate: 'endDate',
		result: 'result',
		performanceResultId: 'categoryAssistance',
		'additionalFields.sponsorId': 'categorySponsor',
		'additionalFields.isHandledByExternalSponsor': 'isHandledByExternalSponsor',
		duration: 'duration',
	};

	return fieldMapping[fieldName] ?? fieldName;
};
